'use client';

import { useState, useEffect } from 'react';
import FAQCategory from './FAQCategory';
import SearchBar from './SearchBar';
import QuickLinks from './QuickLinks';
import LiveChatWidget from './LiveChatWidget';
import AdditionalQuestionForm from './AdditionalQuestionForm';

interface FAQItem {
  id: number;
  question: string;
  answer: string;
  helpful: number;
  notHelpful: number;
  relatedResources: string[];
  category: string;
  keywords: string[];
}

interface QuickLink {
  id: number;
  title: string;
  description: string;
  icon: string;
  count: number;
  category: string;
}

export default function FAQHubInteractive() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [feedbackMessage, setFeedbackMessage] = useState('');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const quickLinks: QuickLink[] = [
    {
      id: 1,
      title: "Getting Started",
      description: "New to financial planning? Start here for basics and first steps.",
      icon: "RocketLaunchIcon",
      count: 12,
      category: "getting-started"
    },
    {
      id: 2,
      title: "Licensing Process",
      description: "Everything about obtaining your financial services license.",
      icon: "AcademicCapIcon",
      count: 18,
      category: "licensing"
    },
    {
      id: 3,
      title: "Business Opportunities",
      description: "Learn about building your own financial services practice.",
      icon: "BriefcaseIcon",
      count: 15,
      category: "business"
    },
    {
      id: 4,
      title: "Compliance & Legal",
      description: "Regulatory requirements and ethical practice guidelines.",
      icon: "ShieldCheckIcon",
      count: 10,
      category: "compliance"
    }
  ];

  const allFAQs: FAQItem[] = [
    {
      id: 1,
      question: "What services does Code Spring Financials offer?",
      answer: "We provide comprehensive financial planning services including Insurance Protection, Retirement Strategies, Business Strategies, Mutual Funds, and Provider Solutions. We also offer licensing education and business development opportunities for aspiring financial professionals.",
      helpful: 45,
      notHelpful: 3,
      relatedResources: ["Services Overview", "Licensing Education"],
      category: "getting-started",
      keywords: ["services", "offerings", "what do you do", "financial planning"]
    },
    {
      id: 2,
      question: "How do I get started with financial planning?",
      answer: "Getting started is simple! Schedule a complimentary consultation with one of our licensed professionals. We'll assess your current financial situation, discuss your goals, and create a personalized plan that meets your needs. No obligation required.",
      helpful: 38,
      notHelpful: 2,
      relatedResources: ["Contact Us", "Services Overview"],
      category: "getting-started",
      keywords: ["start", "begin", "first step", "consultation"]
    },
    {
      id: 3,
      question: "What are the requirements to become a licensed financial professional?",
      answer: "Requirements vary by state but generally include being 18+ years old, passing a background check, completing pre-licensing education (typically 20-40 hours), and passing your state's licensing exam. We provide comprehensive support throughout the entire process.",
      helpful: 52,
      notHelpful: 5,
      relatedResources: ["Licensing Education Center", "State Requirements"],
      category: "licensing",
      keywords: ["license", "requirements", "become agent", "qualifications"]
    },
    {
      id: 4,
      question: "How long does it take to get licensed?",
      answer: "The licensing timeline typically ranges from 4-8 weeks depending on your state, study pace, and exam scheduling. Our accelerated programs can help motivated individuals complete the process in as little as 3-4 weeks.",
      helpful: 41,
      notHelpful: 4,
      relatedResources: ["Licensing Timeline Calculator", "Study Materials"],
      category: "licensing",
      keywords: ["timeline", "how long", "duration", "time to license"]
    },
    {
      id: 5,
      question: "What does the licensing exam cover?",
      answer: "State licensing exams cover insurance fundamentals, policy types, regulations, ethics, and state-specific laws. Our comprehensive study materials include practice exams, video tutorials, and one-on-one mentorship to ensure you're fully prepared.",
      helpful: 36,
      notHelpful: 2,
      relatedResources: ["Study Materials", "Practice Exams"],
      category: "licensing",
      keywords: ["exam", "test", "what to study", "exam content"]
    },
    {
      id: 6,
      question: "What kind of income can I expect as a financial professional?",
      answer: "Income varies based on effort, market, and business model. New agents typically earn $40,000-$60,000 in their first year, with experienced professionals earning $80,000-$150,000+. Top performers building teams can exceed $200,000 annually. We provide transparent income projections and mentorship.",
      helpful: 67,
      notHelpful: 8,
      relatedResources: ["Income Calculator", "Success Stories"],
      category: "business",
      keywords: ["income", "earnings", "salary", "how much money"]
    },
    {
      id: 7,
      question: "Do I need prior financial experience to join?",
      answer: "No prior financial experience is required! We welcome career changers from all backgrounds. Our comprehensive training program covers everything from basic financial concepts to advanced sales techniques. What matters most is your commitment to helping others and willingness to learn.",
      helpful: 44,
      notHelpful: 3,
      relatedResources: ["Training Program", "Success Stories"],
      category: "business",
      keywords: ["experience", "background", "career change", "no experience"]
    },
    {
      id: 8,
      question: "What support do you provide to new agents?",
      answer: "New agents receive comprehensive support including one-on-one mentorship, weekly training sessions, marketing materials, CRM systems, lead generation assistance, and ongoing professional development. You're never alone in building your practice.",
      helpful: 49,
      notHelpful: 2,
      relatedResources: ["Mentorship Program", "Training Resources"],
      category: "business",
      keywords: ["support", "help", "mentorship", "training"]
    },
    {
      id: 9,
      question: "What are the compliance requirements for financial professionals?",
      answer: "Financial professionals must maintain active state licenses, complete continuing education requirements (typically 24 hours every 2 years), follow ethical guidelines, maintain E&O insurance, and comply with state and federal regulations. We provide ongoing compliance training and support.",
      helpful: 33,
      notHelpful: 4,
      relatedResources: ["Compliance Resources", "Continuing Education"],
      category: "compliance",
      keywords: ["compliance", "regulations", "requirements", "legal"]
    },
    {
      id: 10,
      question: "How do you ensure ethical practices?",
      answer: "Ethics are foundational to our business. We require all agents to complete ethics training, follow our code of conduct, prioritize client interests, maintain transparency in all transactions, and participate in regular compliance reviews. We have zero tolerance for unethical behavior.",
      helpful: 38,
      notHelpful: 1,
      relatedResources: ["Code of Conduct", "Ethics Training"],
      category: "compliance",
      keywords: ["ethics", "ethical", "integrity", "trust"]
    },
    {
      id: 11,
      question: "What happens if I don't pass the licensing exam?",
      answer: "Don't worry! Most states allow multiple exam attempts. We provide additional study support, identify knowledge gaps, and help you prepare for your retake. Our pass rate is significantly higher than the national average because of our comprehensive preparation program.",
      helpful: 29,
      notHelpful: 3,
      relatedResources: ["Study Support", "Retake Preparation"],
      category: "licensing",
      keywords: ["fail", "didn't pass", "retake", "exam failure"]
    },
    {
      id: 12,
      question: "Can I work part-time while building my practice?",
      answer: "Absolutely! Many successful agents start part-time while maintaining other employment. Our flexible business model allows you to build at your own pace. However, full-time commitment typically leads to faster growth and higher income potential.",
      helpful: 42,
      notHelpful: 5,
      relatedResources: ["Business Models", "Success Stories"],
      category: "business",
      keywords: ["part-time", "flexible", "schedule", "work hours"]
    }
  ];

  const searchSuggestions = [
    "How do I get started with financial planning?",
    "What are the licensing requirements?",
    "How long does it take to get licensed?",
    "What kind of income can I expect?",
    "Do I need prior experience?",
    "What support do you provide?",
    "What are the compliance requirements?",
    "Can I work part-time?"
  ];

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/faq-hub/components/FAQHubInteractive.tsx:211:6" data-component-path="src/app/faq-hub/components/FAQHubInteractive.tsx" data-component-line="211" data-component-end-line="234" data-component-file="FAQHubInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
        <div data-component-id="src/app/faq-hub/components/FAQHubInteractive.tsx:212:8" data-component-path="src/app/faq-hub/components/FAQHubInteractive.tsx" data-component-line="212" data-component-end-line="233" data-component-file="FAQHubInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22container%20mx-auto%20px-4%20py-12%22%7D" className="container mx-auto px-4 py-12">
          <div data-component-id="src/app/faq-hub/components/FAQHubInteractive.tsx:213:10" data-component-path="src/app/faq-hub/components/FAQHubInteractive.tsx" data-component-line="213" data-component-end-line="220" data-component-file="FAQHubInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20mb-12%22%7D" className="text-center mb-12">
            <h1 data-component-id="src/app/faq-hub/components/FAQHubInteractive.tsx:214:12" data-component-path="src/app/faq-hub/components/FAQHubInteractive.tsx" data-component-line="214" data-component-end-line="216" data-component-file="FAQHubInteractive.tsx" data-component-name="h1" data-component-content="%7B%22elementName%22%3A%22h1%22%2C%22className%22%3A%22font-headline%20text-4xl%20md%3Atext-5xl%20font-bold%20text-primary%20mb-4%22%2C%22textContent%22%3A%22Frequently%20Asked%20Questions%22%7D" className="font-headline text-4xl md:text-5xl font-bold text-primary mb-4">
              Frequently Asked Questions
            </h1>
            <p data-component-id="src/app/faq-hub/components/FAQHubInteractive.tsx:217:12" data-component-path="src/app/faq-hub/components/FAQHubInteractive.tsx" data-component-line="217" data-component-end-line="219" data-component-file="FAQHubInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22font-body%20text-lg%20text-muted-foreground%20max-w-2xl%20mx-auto%22%2C%22textContent%22%3A%22Find%20answers%20to%20common%20questions%20about%20our%20services%2C%20licensing%20process%2C%20and%20business%20opportunities%22%7D" className="font-body text-lg text-muted-foreground max-w-2xl mx-auto">
              Find answers to common questions about our services, licensing process, and business opportunities
            </p>
          </div>

          <div data-component-id="src/app/faq-hub/components/FAQHubInteractive.tsx:222:10" data-component-path="src/app/faq-hub/components/FAQHubInteractive.tsx" data-component-line="222" data-component-end-line="228" data-component-file="FAQHubInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-12%22%7D" className="mb-12">
            <SearchBar data-component-id="src/app/faq-hub/components/FAQHubInteractive.tsx:223:12" data-component-path="src/app/faq-hub/components/FAQHubInteractive.tsx" data-component-line="223" data-component-end-line="227" data-component-file="FAQHubInteractive.tsx" data-component-name="SearchBar" data-component-content="%7B%22elementName%22%3A%22SearchBar%22%7D" 
              onSearch={() => {}} 
              suggestions={[]} 
              onSuggestionClick={() => {}}
            />
          </div>

          <div data-component-id="src/app/faq-hub/components/FAQHubInteractive.tsx:230:10" data-component-path="src/app/faq-hub/components/FAQHubInteractive.tsx" data-component-line="230" data-component-end-line="232" data-component-file="FAQHubInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-12%22%7D" className="mb-12">
            <QuickLinks data-component-id="src/app/faq-hub/components/FAQHubInteractive.tsx:231:12" data-component-path="src/app/faq-hub/components/FAQHubInteractive.tsx" data-component-line="231" data-component-end-line="231" data-component-file="FAQHubInteractive.tsx" data-component-name="QuickLinks" data-component-content="%7B%22elementName%22%3A%22QuickLinks%22%7D" links={quickLinks} onLinkClick={() => {}} />
          </div>
        </div>
      </div>
    );
  }

  const handleSearch = (query: string) => {
    setSearchQuery(query.toLowerCase());
    setSelectedCategory(null);
  };

  const handleSuggestionClick = (suggestion: string) => {
    setSearchQuery(suggestion.toLowerCase());
    setSelectedCategory(null);
  };

  const handleQuickLinkClick = (linkId: number) => {
    const link = quickLinks.find(l => l.id === linkId);
    if (link) {
      setSelectedCategory(link.category);
      setSearchQuery('');
      window.scrollTo({ top: 400, behavior: 'smooth' });
    }
  };

  const handleFeedback = (faqId: number, isHelpful: boolean) => {
    setFeedbackMessage(`Thank you for your feedback!`);
    setTimeout(() => setFeedbackMessage(''), 3000);
  };

  const handleResourceClick = (resource: string) => {
    console.log(`Navigate to: ${resource}`);
  };

  const handleChatOpen = () => {
    console.log('Opening live chat...');
  };

  const handleQuestionSubmit = (question: string, email: string) => {
    console.log('Question submitted:', { question, email });
  };

  const filteredFAQs = allFAQs.filter(faq => {
    const matchesSearch = searchQuery === '' || 
      faq.question.toLowerCase().includes(searchQuery) ||
      faq.answer.toLowerCase().includes(searchQuery) ||
      faq.keywords.some(keyword => keyword.includes(searchQuery));
    
    const matchesCategory = selectedCategory === null || faq.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  const categories = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      description: 'Essential information for newcomers to financial planning and our services',
      faqs: filteredFAQs.filter(faq => faq.category === 'getting-started')
    },
    {
      id: 'licensing',
      title: 'Licensing Process',
      description: 'Everything you need to know about obtaining your financial services license',
      faqs: filteredFAQs.filter(faq => faq.category === 'licensing')
    },
    {
      id: 'business',
      title: 'Business Opportunities',
      description: 'Information about building and growing your financial services practice',
      faqs: filteredFAQs.filter(faq => faq.category === 'business')
    },
    {
      id: 'compliance',
      title: 'Compliance & Legal',
      description: 'Regulatory requirements and ethical practice guidelines',
      faqs: filteredFAQs.filter(faq => faq.category === 'compliance')
    }
  ];

  return (
    <div data-component-id="src/app/faq-hub/components/FAQHubInteractive.tsx:313:4" data-component-path="src/app/faq-hub/components/FAQHubInteractive.tsx" data-component-line="313" data-component-end-line="388" data-component-file="FAQHubInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
      <div data-component-id="src/app/faq-hub/components/FAQHubInteractive.tsx:314:6" data-component-path="src/app/faq-hub/components/FAQHubInteractive.tsx" data-component-line="314" data-component-end-line="385" data-component-file="FAQHubInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22container%20mx-auto%20px-4%20py-12%22%7D" className="container mx-auto px-4 py-12">
        <div data-component-id="src/app/faq-hub/components/FAQHubInteractive.tsx:315:8" data-component-path="src/app/faq-hub/components/FAQHubInteractive.tsx" data-component-line="315" data-component-end-line="322" data-component-file="FAQHubInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20mb-12%22%7D" className="text-center mb-12">
          <h1 data-component-id="src/app/faq-hub/components/FAQHubInteractive.tsx:316:10" data-component-path="src/app/faq-hub/components/FAQHubInteractive.tsx" data-component-line="316" data-component-end-line="318" data-component-file="FAQHubInteractive.tsx" data-component-name="h1" data-component-content="%7B%22elementName%22%3A%22h1%22%2C%22className%22%3A%22font-headline%20text-4xl%20md%3Atext-5xl%20font-bold%20text-primary%20mb-4%22%2C%22textContent%22%3A%22Frequently%20Asked%20Questions%22%7D" className="font-headline text-4xl md:text-5xl font-bold text-primary mb-4">
            Frequently Asked Questions
          </h1>
          <p data-component-id="src/app/faq-hub/components/FAQHubInteractive.tsx:319:10" data-component-path="src/app/faq-hub/components/FAQHubInteractive.tsx" data-component-line="319" data-component-end-line="321" data-component-file="FAQHubInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22font-body%20text-lg%20text-muted-foreground%20max-w-2xl%20mx-auto%22%2C%22textContent%22%3A%22Find%20answers%20to%20common%20questions%20about%20our%20services%2C%20licensing%20process%2C%20and%20business%20opportunities%22%7D" className="font-body text-lg text-muted-foreground max-w-2xl mx-auto">
            Find answers to common questions about our services, licensing process, and business opportunities
          </p>
        </div>

        <div data-component-id="src/app/faq-hub/components/FAQHubInteractive.tsx:324:8" data-component-path="src/app/faq-hub/components/FAQHubInteractive.tsx" data-component-line="324" data-component-end-line="332" data-component-file="FAQHubInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-12%22%7D" className="mb-12">
          <SearchBar data-component-id="src/app/faq-hub/components/FAQHubInteractive.tsx:325:10" data-component-path="src/app/faq-hub/components/FAQHubInteractive.tsx" data-component-line="325" data-component-end-line="331" data-component-file="FAQHubInteractive.tsx" data-component-name="SearchBar" data-component-content="%7B%22elementName%22%3A%22SearchBar%22%7D" 
            onSearch={handleSearch}
            suggestions={searchSuggestions.filter(s => 
              s.toLowerCase().includes(searchQuery) && searchQuery.length > 0
            )}
            onSuggestionClick={handleSuggestionClick}
          />
        </div>

        {!searchQuery && !selectedCategory && (
          <div data-component-id="src/app/faq-hub/components/FAQHubInteractive.tsx:335:10" data-component-path="src/app/faq-hub/components/FAQHubInteractive.tsx" data-component-line="335" data-component-end-line="337" data-component-file="FAQHubInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-12%22%7D" className="mb-12">
            <QuickLinks data-component-id="src/app/faq-hub/components/FAQHubInteractive.tsx:336:12" data-component-path="src/app/faq-hub/components/FAQHubInteractive.tsx" data-component-line="336" data-component-end-line="336" data-component-file="FAQHubInteractive.tsx" data-component-name="QuickLinks" data-component-content="%7B%22elementName%22%3A%22QuickLinks%22%7D" links={quickLinks} onLinkClick={handleQuickLinkClick} />
          </div>
        )}

        {feedbackMessage && (
          <div data-component-id="src/app/faq-hub/components/FAQHubInteractive.tsx:341:10" data-component-path="src/app/faq-hub/components/FAQHubInteractive.tsx" data-component-line="341" data-component-end-line="343" data-component-file="FAQHubInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-6%20p-4%20bg-success%2F10%20border%20border-success%2F20%20rounded-lg%20text-center%22%7D" className="mb-6 p-4 bg-success/10 border border-success/20 rounded-lg text-center">
            <p data-component-id="src/app/faq-hub/components/FAQHubInteractive.tsx:342:12" data-component-path="src/app/faq-hub/components/FAQHubInteractive.tsx" data-component-line="342" data-component-end-line="342" data-component-file="FAQHubInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22font-body%20text-sm%20text-success%20font-medium%22%7D" className="font-body text-sm text-success font-medium">{feedbackMessage}</p>
          </div>
        )}

        {searchQuery && filteredFAQs.length === 0 && (
          <div data-component-id="src/app/faq-hub/components/FAQHubInteractive.tsx:347:10" data-component-path="src/app/faq-hub/components/FAQHubInteractive.tsx" data-component-line="347" data-component-end-line="363" data-component-file="FAQHubInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20py-12%22%7D" className="text-center py-12">
            <div data-component-id="src/app/faq-hub/components/FAQHubInteractive.tsx:348:12" data-component-path="src/app/faq-hub/components/FAQHubInteractive.tsx" data-component-line="348" data-component-end-line="350" data-component-file="FAQHubInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-16%20h-16%20bg-muted%20rounded-full%20flex%20items-center%20justify-center%20mx-auto%20mb-4%22%7D" className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <span data-component-id="src/app/faq-hub/components/FAQHubInteractive.tsx:349:14" data-component-path="src/app/faq-hub/components/FAQHubInteractive.tsx" data-component-line="349" data-component-end-line="349" data-component-file="FAQHubInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-3xl%22%2C%22textContent%22%3A%22%F0%9F%94%8D%22%7D" className="text-3xl">🔍</span>
            </div>
            <h2 data-component-id="src/app/faq-hub/components/FAQHubInteractive.tsx:351:12" data-component-path="src/app/faq-hub/components/FAQHubInteractive.tsx" data-component-line="351" data-component-end-line="353" data-component-file="FAQHubInteractive.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22font-headline%20text-2xl%20font-semibold%20text-primary%20mb-2%22%2C%22textContent%22%3A%22No%20Results%20Found%22%7D" className="font-headline text-2xl font-semibold text-primary mb-2">
              No Results Found
            </h2>
            <p data-component-id="src/app/faq-hub/components/FAQHubInteractive.tsx:354:12" data-component-path="src/app/faq-hub/components/FAQHubInteractive.tsx" data-component-line="354" data-component-end-line="356" data-component-file="FAQHubInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22font-body%20text-sm%20text-muted-foreground%20mb-6%22%2C%22textContent%22%3A%22We%20couldn't%20find%20any%20FAQs%20matching%20%5C%22%20%5C%22%22%7D" className="font-body text-sm text-muted-foreground mb-6">
              We couldn't find any FAQs matching "{searchQuery}"
            </p>
            <button data-component-id="src/app/faq-hub/components/FAQHubInteractive.tsx:357:12" data-component-path="src/app/faq-hub/components/FAQHubInteractive.tsx" data-component-line="357" data-component-end-line="362" data-component-file="FAQHubInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22px-6%20py-2%20bg-accent%20text-primary-foreground%20font-body%20font-medium%20text-sm%20rounded-md%20hover%3Ascale-105%20transition-transform%22%2C%22textContent%22%3A%22Clear%20Search%22%7D"
              onClick={() => setSearchQuery('')}
              className="px-6 py-2 bg-accent text-primary-foreground font-body font-medium text-sm rounded-md hover:scale-105 transition-transform"
            >
              Clear Search
            </button>
          </div>
        )}

        <div data-component-id="src/app/faq-hub/components/FAQHubInteractive.tsx:366:8" data-component-path="src/app/faq-hub/components/FAQHubInteractive.tsx" data-component-line="366" data-component-end-line="380" data-component-file="FAQHubInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-8%20mb-12%22%7D" className="space-y-8 mb-12">
          {categories.map(category => {
            if (category.faqs.length === 0) return null;
            
            return (
              <FAQCategory data-component-id="src/app/faq-hub/components/FAQHubInteractive.tsx:371:14" data-component-path="src/app/faq-hub/components/FAQHubInteractive.tsx" data-component-line="371" data-component-end-line="377" data-component-file="FAQHubInteractive.tsx" data-component-name="FAQCategory" data-component-content="%7B%22elementName%22%3A%22FAQCategory%22%7D"
                key={category.id}
                title={category.title}
                description={category.description}
                faqs={category.faqs}
                onResourceClick={handleResourceClick}
              />
            );
          })}
        </div>

        <div data-component-id="src/app/faq-hub/components/FAQHubInteractive.tsx:382:8" data-component-path="src/app/faq-hub/components/FAQHubInteractive.tsx" data-component-line="382" data-component-end-line="384" data-component-file="FAQHubInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-2xl%20mx-auto%22%7D" className="max-w-2xl mx-auto">
          <AdditionalQuestionForm data-component-id="src/app/faq-hub/components/FAQHubInteractive.tsx:383:10" data-component-path="src/app/faq-hub/components/FAQHubInteractive.tsx" data-component-line="383" data-component-end-line="383" data-component-file="FAQHubInteractive.tsx" data-component-name="AdditionalQuestionForm" data-component-content="%7B%22elementName%22%3A%22AdditionalQuestionForm%22%7D" onSubmit={handleQuestionSubmit} />
        </div>
      </div>

      <LiveChatWidget data-component-id="src/app/faq-hub/components/FAQHubInteractive.tsx:387:6" data-component-path="src/app/faq-hub/components/FAQHubInteractive.tsx" data-component-line="387" data-component-end-line="387" data-component-file="FAQHubInteractive.tsx" data-component-name="LiveChatWidget" data-component-content="%7B%22elementName%22%3A%22LiveChatWidget%22%7D" onChatOpen={handleChatOpen} />
    </div>
  );
}